import { useMutation, useQuery } from "@apollo/client";
import { Button, Col, Form, Layout, Row, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import ApplicationPanelFormContainer from "components/ApplicationPanel/styles";
import { Loading } from "components/Loading";
import {
  ADD_OR_UPDATE_PICTURE_ALBUM_MUTATION,
  DELETE_PICTURE_ALBUM_MUTATION,
} from "graphql/manageLandingPage/mutations";
import { GET_ALBUM_PICTURES_LIST } from "graphql/queries";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import PictureAlbumFormList from "./PictureAlbumFormList";

const { Title } = Typography;

const PictureAlbumForm = () => {
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const errorMessageRef = useRef(null);
  const successMessageRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);

  const {
    loading: albumPicturesListLoading,
    data: albumPicturesListData,
    refetch: albumPicturesListRefetch,
  } = useQuery(GET_ALBUM_PICTURES_LIST, {});

  const [addOrUpdatePictureAlbumMutation] = useMutation(ADD_OR_UPDATE_PICTURE_ALBUM_MUTATION, {
    onCompleted: (data) => {
      setSubmitting(false);
      if (data.addOrUpdatePictureAlbum.success) {
        setErrorMessage(null);
        setSuccessMessage(t("dashboard.savedSuccessfullyMessage"));
        scrollToSuccess();
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000); // Clear success message after 5 seconds
        albumPicturesListRefetch();
      } else {
        setErrorMessage(data.addOrUpdatePictureAlbum.errors[0]);
        setSuccessMessage(null);
        scrollToError();
      }
    },
  });

  const [deletePictureAlbumMutation] = useMutation(DELETE_PICTURE_ALBUM_MUTATION, {
    onCompleted: (data) => {
      if (data.deletePictureAlbum.success) {
        setInputFieldsLoading(true);
        setErrorMessage(null);
        setSuccessMessage(
          t("dashboard.landingPageSettings.pictureAlbumSection.deletedSuccessfullyMessage"),
        );
        scrollToSuccess();
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000); // Clear success message after 5 seconds
        albumPicturesListRefetch();
      } else {
        setErrorMessage(data.addOrUpdatePictureAlbum.errors[0]);
        setSuccessMessage(null);
        scrollToError();
      }
    },
  });

  const onFinish = () => {
    setSubmitting(true);

    addOrUpdatePictureAlbumMutation({
      variables: { pictureAlbum: inputFields },
    });
  };

  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const scrollToError = () => {
    if (errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const scrollToSuccess = () => {
    if (successMessageRef.current) {
      successMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // wait till the form initial values are calculated, as the form won't re-render.
  const [inputFieldsLoading, setInputFieldsLoading] = useState(true);
  const [inputFields, setInputFields] = useState([]);

  useEffect(() => {
    if (!albumPicturesListLoading) {
      setInputFieldsLoading(true);
      const data = albumPicturesListData.albumPicturesList.map((obj) => {
        return {
          id: obj.id,
          descriptionAr: obj.descriptionAr,
          descriptionEn: obj.descriptionEn,
          active: obj.active,
        };
      });

      if (data.length === 0)
        data.push({
          picture: null,
          descriptionAr: "",
          descriptionEn: "",
          active: false,
        });

      setInputFields(data);
      setInputFieldsLoading(false);
    }
  }, [albumPicturesListData, albumPicturesListLoading]);

  if (albumPicturesListLoading || inputFieldsLoading) return <Loading />;

  return (
    <ApplicationPanelFormContainer>
      <Layout className="p-3 p-md-5 m-0">
        <Content>
          <Row gutter={[24, 0]}>
            <Col xs={24}>
              <Title
                className="mb-5 mt-0"
                style={{
                  fontSize: "45px",
                  whiteSpace: "break-spaces",
                }}
              >
                {t("dashboard.sideNav.pictureAlbumTab")}
              </Title>
            </Col>
          </Row>

          <Form
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            className="row-col"
            scrollToFirstError={true}
          >
            <div ref={errorMessageRef}></div> {/* Error message ref */}
            {errorMessage && <p className="text-danger english-field">{errorMessage}</p>}
            <div ref={successMessageRef}></div> {/* Success message ref */}
            {successMessage && <p className="text-success">{successMessage}</p>}
            <PictureAlbumFormList
              inputFields={inputFields}
              setInputFields={setInputFields}
              data={albumPicturesListData.albumPicturesList}
              deletePictureAlbumMutation={deletePictureAlbumMutation}
            />
            <Row gutter={[24, 0]} justify="center">
              <Col xs={24}>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: "100%" }}
                    loading={submitting}
                    disabled={submitting}
                  >
                    {t("dashboard.applicationPanelForm.saveBtn")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Content>
      </Layout>
    </ApplicationPanelFormContainer>
  );
};

export default PictureAlbumForm;
